import { QUALITY_SCORE_GOOD_THRESHOLD, QUALITY_SCORE_POOR_THRESHOLD } from '@shared/constants';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { MouseEvent, ReactNode } from 'react';

type QualityVerdict = { label: string; className: string; dotClassName: string };

export const getQualityVerdict = (score: number | null | undefined): QualityVerdict | null => {
  if (typeof score !== 'number') return null;
  if (score >= QUALITY_SCORE_GOOD_THRESHOLD) {
    return {
      label: 'Good',
      className: 'bg-customGreen/15 text-customGreen border-customGreen/40',
      dotClassName: 'bg-customGreen',
    };
  }
  if (score >= QUALITY_SCORE_POOR_THRESHOLD) {
    return {
      label: 'OK',
      className: 'bg-customGold/15 text-customGold border-customGold/40',
      dotClassName: 'bg-customGold',
    };
  }
  return {
    label: 'Poor',
    className: 'bg-customRed/15 text-customRed border-customRed/40',
    dotClassName: 'bg-customRed',
  };
};

interface QualityScoreBadgeProps {
  score: number | null | undefined;
  size?: 'small' | 'large';
  prefix?: ReactNode;
  suffix?: ReactNode;
  emptyText?: string;
  className?: string;
  onClick?: () => void;
}

const QualityScoreBadge = (props: QualityScoreBadgeProps) => {
  const { score, size = 'small', prefix, suffix, emptyText = '—', className, onClick } = props;
  const verdict = getQualityVerdict(score);

  if (!verdict || typeof score !== 'number') {
    return <span className={mergeClassNames('text-customGrey', className)}>{emptyText}</span>;
  }

  const clickHandler = onClick
    ? (event: MouseEvent) => {
        event.stopPropagation();
        onClick();
      }
    : undefined;

  return (
    <span
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={clickHandler}
      title={`AI quality score ${score.toFixed(
        2
      )} / 5 · good ≥ ${QUALITY_SCORE_GOOD_THRESHOLD}, poor < ${QUALITY_SCORE_POOR_THRESHOLD}`}
      className={mergeClassNames(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border font-semibold',
        size === 'large' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs',
        onClick ? 'cursor-pointer hover:brightness-125' : '',
        verdict.className,
        className
      )}
    >
      <span className={mergeClassNames('h-2 w-2 rounded-full', verdict.dotClassName)} />
      {prefix}
      <span>{verdict.label}</span>
      <span className="tabular-nums opacity-80">{score.toFixed(2)}</span>
      {suffix}
    </span>
  );
};

export default QualityScoreBadge;
